import { Form, Steps } from 'antd';
import { useTranslation } from 'react-i18next';
import type { OemPartnerGroupDto } from '@evimo/client/models';
import { SlaModalShell } from '~/app/landing/sla/shared/components/SlaModalShell';
import { SlaPartnerModalFooter } from '~/app/landing/sla/shared/components/SlaPartnerModalFooter';
import { useSlaWizardStepItems } from '~/app/landing/sla/shared/hooks/useSlaWizardStepItems';
import { resolveSlaModalLayout } from '~/app/landing/sla/shared/utils/sla-modal-layout.utils';
import { OemPartnerModalBody } from '@/app/landing/sla/oems/OemPartnerModal/components/OemPartnerModalBody';
import { useOemPartnerModal } from '@/app/landing/sla/oems/OemPartnerModal/hooks/useOemPartnerModal';

type Props = {
  open: boolean;
  organizationId: string;
  editingRow: OemPartnerGroupDto | null;
  onClose: () => void;
  onSuccess: () => void;
};

export function OemPartnerModal({
  open,
  organizationId,
  editingRow,
  onClose,
  onSuccess,
}: Props) {
  const { t } = useTranslation('sla');
  const steps = useSlaWizardStepItems('oem');
  const modal = useOemPartnerModal({
    open,
    organizationId,
    editingRow,
    onSuccess,
  });

  const modalLayout = resolveSlaModalLayout({
    partnerKind: 'oem',
    isEdit: modal.isEdit,
    createStep: modal.step,
  });

  return (
    <SlaModalShell
      open={open}
      width={modalLayout.width}
      onClose={onClose}
      title={modal.isEdit ? t('oems.editTitle') : t('oems.createTitle')}
      headerExtra={
        !modal.isEdit ? (
          <Steps
            current={modal.step}
            items={steps}
            size="small"
            style={{ marginBottom: '1.25rem' }}
          />
        ) : undefined
      }
      footer={
        <SlaPartnerModalFooter
          isEdit={modal.isEdit}
          step={modal.step}
          submitting={modal.submitting}
          canProceedFromPartnerStep={modal.canProceedFromPartnerStep}
          onClose={onClose}
          onBack={() => modal.setStep((current) => current - 1)}
          onNext={modal.handleNext}
          onSubmit={modal.handleSubmit}
        />
      }
      pinFooter={modalLayout.pinFooter}
      shellMaxHeight={modalLayout.shellMaxHeight}
      scrollBody={modalLayout.scrollBody}
      maxBodyHeight={modalLayout.maxBodyHeight}
    >
      <Form
        form={modal.form}
        layout="vertical"
        preserve
        className="sla-modal-form"
      >
        <OemPartnerModalBody
          organizationId={organizationId}
          isEdit={modal.isEdit}
          step={modal.step}
          selectedPartner={modal.selectedPartner}
          onPartnerChange={modal.setSelectedPartner}
          contractSnapshot={modal.contractSnapshot}
        />
      </Form>
    </SlaModalShell>
  );
}
